import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { TriangleAlert } from '@/components/icons';
import { Button } from './Button';
import { Dialog } from './Dialog';

export interface ConfirmOptions {
  title: ReactNode;
  /** What happens, in plain words. */
  message?: ReactNode;
  /** Label of the confirm button, e.g. "Delete report". */
  confirmLabel?: string;
  cancelLabel?: string;
  /** danger: red confirm button and a warning icon (default, for deletions). */
  tone?: 'danger' | 'default';
}

type Confirm = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<Confirm | null>(null);

/**
 * In-app replacement for window.confirm. `await confirm({...})` resolves true on confirm, false on
 * cancel, Esc, the close button or a backdrop click. Focus starts on Cancel so Enter never deletes by accident.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<(ConfirmOptions & { resolve: (ok: boolean) => void }) | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const confirm = useCallback<Confirm>(
    (options) =>
      new Promise<boolean>((resolve) => {
        setRequest((prev) => {
          prev?.resolve(false); // a newer question replaces an unanswered one
          return { ...options, resolve };
        });
      }),
    [],
  );

  const answer = (ok: boolean) => {
    request?.resolve(ok);
    setRequest(null);
  };

  useEffect(() => {
    // After showModal() has run (it focuses the first control, the close button).
    if (request) requestAnimationFrame(() => cancelRef.current?.focus());
  }, [request]);

  const danger = (request?.tone ?? 'danger') === 'danger';
  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog
        open={!!request}
        onClose={() => answer(false)}
        size="sm"
        title={
          <span className="flex items-center gap-2.5">
            {danger && (
              <span className="grid size-8 shrink-0 place-items-center rounded-full bg-danger/15 text-danger">
                <TriangleAlert className="size-4" aria-hidden />
              </span>
            )}
            {request?.title}
          </span>
        }
        footer={
          <>
            <Button ref={cancelRef} variant="secondary" onClick={() => answer(false)}>
              {request?.cancelLabel ?? 'Cancel'}
            </Button>
            <Button variant={danger ? 'danger' : 'primary'} onClick={() => answer(true)}>
              {request?.confirmLabel ?? (danger ? 'Delete' : 'Confirm')}
            </Button>
          </>
        }
      >
        {request?.message && <div className="text-sm text-fg-secondary">{request.message}</div>}
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): Confirm {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return ctx;
}
